<script setup lang="ts">
import { computed, onMounted, ref } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useCompanyStore } from '@Stores/company.store';
import { useDashboardStore } from '@Stores/dashboard.store';
import useCompanyImageBaseUrl from '@Use/useCompanyImageBaseUrl';
import useDealLogoUrl from '@Use/useDealLogoUrl';
import { usePanelReveal } from '@Use/usePanelReveal';
import { useRcsConsent } from '@Use/useRcsConsent';
import { useTaskSheet } from '@Use/useTaskSheet';
import { useTaskNavigation } from '@Use/useTaskNavigation';
import { useTaskPriming } from '@Use/useTaskPriming';
import { isMatchTask } from '@Src/shared/utils/resolveEffectiveType';

// import components
import AppFooter from '@Components/AppFooter.vue';
import { PortalHeader, type Location } from '@Shared';
import DealHeader from '@Components/DealHeader.vue';
import EmptyState from '@Components/EmptyState.vue';
import PropertySelector from '@Components/PropertySelector.vue';
import TaskCard from '@Components/TaskCard.vue';
import ParticipantPaymentCard from '@Components/ParticipantPaymentCard.vue';
import PaymentPurposeSheet from '@Components/PaymentPurposeSheet.vue';
import { useParticipantInitiatedPayment } from '@Use/useParticipantInitiatedPayment';
import TaskSheet from '@Components/TaskSheet.vue';
import TaskPrimingSheet from '@Components/TaskPrimingSheet.vue';
import WelcomeSheet from '@Components/welcome/WelcomeSheet.vue';

const route = useRoute();
const router = useRouter();
const companyStore = useCompanyStore();
const dashboardStore = useDashboardStore();
const companyImageBaseUrl = useCompanyImageBaseUrl();
const companyLogoUrl = useDealLogoUrl(companyImageBaseUrl, companyStore, dashboardStore);

// PS-1144 — when the URL carries a deep-linked taskId we are only passing through
// this route to resolve the task; keep the loader up so the task LIST never paints.
// Seeded synchronously at setup so the very first render is already suppressed.
const isResolvingDeepLink = ref(Boolean(route.query.taskId));

const isLoading = computed(() => dashboardStore.isLoading || isResolvingDeepLink.value);
const error = computed(() => dashboardStore.error);

const activeDeal = computed(() => {
    const dealId = route.params.dealId as string | undefined;
    if (dealId) return dashboardStore.deals.find((d) => d.dealId === dealId) ?? null;
    return dashboardStore.activeDeal;
});

const sidebarLocation = computed((): Location | null => {
    const locationId = activeDeal.value?.locationId;
    if (!locationId) return null;
    return { id: locationId } as Location;
});

const activeTasks = computed(() => {
    if (!activeDeal.value) return [];
    // BFF publishes activeTasks as the authoritative ordered list (Match
    // pinned to position 0 when any task is locked). Recombining the
    // per-status buckets would lose the pin when Match is in_progress.
    return activeDeal.value.activeTasks;
});

const completedTasks = computed(() => {
    if (!activeDeal.value) return [];
    return [...activeDeal.value.completedTasks];
});

const remainingCount = computed(() => activeTasks.value.length);

const hasNoTasks = computed(() =>
    activeTasks.value.length === 0 && completedTasks.value.length === 0,
);

const {
    purposes: initiablePurposes,
    canInitiatePayment,
    isSheetOpen: isPurposeSheetOpen,
    isStarting: isStartingPayment,
    startError: paymentStartError,
    openSheet: openPurposeSheet,
    closeSheet: closePurposeSheet,
    startPayment,
} = useParticipantInitiatedPayment();

// Staggered reveal — mirrors certifid-vision portal behavior.
// See src/use/usePanelReveal.ts.
const revealContainer = ref<HTMLElement | null>(null);
usePanelReveal(revealContainer);

const welcomeGreeting = computed(() => {
    const name = dashboardStore.participantFirstName;
    return name ? `Welcome, ${name}!` : 'Welcome!';
});

const {
    isOpen: isPrimingOpen,
    pendingTask: primingTask,
    activeConfig: primingConfig,
    closeSheet: closePrimingSheet,
} = useTaskPriming();
const {
    isOpen: isCompletionOpen,
    pendingTask: completionTask,
    activeView: completionView,
    closeSheet: closeTaskSheet,
} = useTaskSheet();
const { executeNavigation, navigateToTask, isNavigating, navigationError } = useTaskNavigation();
const { captureConsent, isCapturing, error: consentError } = useRcsConsent();

const handlePrimingContinue = async () => {
    const task = primingTask.value;
    if (!task) return;
    await executeNavigation(task);
    // On success the host router has navigated away; on error keep the sheet
    // open so the user sees navigationError surfaced inside TaskPrimingSheet.
    if (!navigationError.value) {
        closePrimingSheet();
    }
};

const handleWelcomeSubmit = async (messagingOptIn: boolean) => {
    const activeDeal = dashboardStore.activeDeal;
    if (!activeDeal?.participantId) {
        // No participant context — fall back to local dismiss so the drawer doesn't loop.
        dashboardStore.dismissWelcomeLocally(messagingOptIn);
        return;
    }

    const success = await captureConsent(activeDeal.participantId, messagingOptIn);
    if (success) {
        dashboardStore.dismissWelcomeLocally(messagingOptIn);
        await dashboardStore.loadDashboard();
    }
    // On failure, leave the drawer open so the user can retry.
};

onMounted(async () => {
    const dealId = route.params.dealId as string | undefined;
    if (dealId) dashboardStore.selectDeal(dealId);
    await dashboardStore.loadDashboard();
    await openDeepLinkedTask();
});

// PS-1144 — a finalize-followup email links to the task the email is about, but the
// invite redirect used to land the participant on the deal's task LIST, leaving them
// to hunt for it. The Request BFF now passes `taskId` through /dashboard/invite, and
// InviteView forwards it here. Open that task exactly as a click would.
//
// The query param is read AFTER loadDashboard() so the task is present in the store,
// and it survives the OTP round-trip because it lives in the URL. It is stripped once
// consumed so a later back-navigation doesn't re-open the sheet.
//
// PLAT-3258 — Match is the exception. Its link is the participant's first contact with
// the deal, not a nudge about one task they already know about, so opening it on arrival
// commits them to identity verification before they have seen what it is. The deep link
// still selects the right deal; the participant starts the task themselves.
const openDeepLinkedTask = async (): Promise<void> => {
    const taskId = route.query.taskId as string | undefined;
    if (!taskId) return;

    try {
        // Clear first: navigateToTask may redirect away, and a failed lookup must not
        // leave a sticky param that re-fires on every subsequent mount.
        await router.replace({ query: { ...route.query, taskId: undefined } });

        const task = dashboardStore.deals
            .flatMap((deal) => [...deal.activeTasks, ...deal.completedTasks])
            .find((t) => t.id === taskId);

        if (!task) {
            // Not an error worth surfacing — drop the loader and let the participant
            // use the full list rather than stranding them on a spinner.
            console.warn(`[Dashboard MFE] Deep-linked task ${taskId} not found; staying on the task list.`);
            isResolvingDeepLink.value = false;
            return;
        }

        if (isMatchTask(task)) {
            isResolvingDeepLink.value = false;
            return;
        }

        await navigateToTask(task);
    } catch (e) {
        // Any failure must release the loader — never strand the participant.
        console.error('[Dashboard MFE] Deep-linked task navigation failed:', e);
        isResolvingDeepLink.value = false;
    }
};
</script>

<template>
    <div ref="revealContainer" class="task-view">
        <PortalHeader
            v-if="companyStore.company"
            :company-logo-url="companyLogoUrl"
            :company="companyStore.company"
            :brand="companyStore.brand"
            :company-image-base-url="companyImageBaseUrl"
            :location="sidebarLocation">
            <template #right>
                <PropertySelector />
            </template>
        </PortalHeader>

        <app-page-loader v-if="isLoading" />

        <template v-else-if="error">
            <EmptyState message="Something went wrong loading your tasks. Please try again." />
        </template>

        <template v-else-if="hasNoTasks">
            <EmptyState message="No tasks assigned yet. Check back soon!" />
        </template>

        <template v-else>
            <!-- Mobile: white hero (welcome + property card) -->
            <div class="task-view__hero">
                <h1 class="task-view__welcome">{{ welcomeGreeting }}</h1>
                <DealHeader v-if="activeDeal" :deal="activeDeal" />
            </div>

            <!-- Mobile: gray task body / Desktop: centered column -->
            <div class="task-view__body">
                <div class="task-view__inner">
                    <!-- Desktop-only welcome block -->
                    <div class="task-view__desktop-welcome">
                        <h1 class="task-view__desktop-heading">{{ welcomeGreeting }}</h1>
                        <p v-if="remainingCount > 0" class="task-view__desktop-subtitle">
                            You have {{ remainingCount }}
                            {{ remainingCount === 1 ? 'task' : 'tasks' }} remaining to close on your property.
                        </p>
                    </div>

                    <!-- Desktop-only property card -->
                    <DealHeader v-if="activeDeal" :deal="activeDeal" class="task-view__desktop-deal" data-panel-reveal />

                    <!-- CPT-582: above the heading, not inside the task list. It is an
                         offer rather than an assigned task, and the vision prototype
                         settled this placement as the only entry variant. Hidden unless
                         the flag, the participant's role and the BFF all allow it. -->
                    <ParticipantPaymentCard
                        v-if="canInitiatePayment"
                        class="task-view__payment-entry"
                        :error-message="paymentStartError"
                        :offered-purposes="initiablePurposes.map((purpose) => purpose.value)"
                        data-panel-reveal
                        @open="openPurposeSheet"
                    />

                    <h2 class="task-view__tasks-heading" data-panel-reveal>Your tasks</h2>

                    <div class="task-view__task-list">
                        <TaskCard
                            v-for="task in activeTasks"
                            :key="task.id"
                            :task="task"
                            data-panel-reveal
                        />
                    </div>

                    <section v-if="completedTasks.length > 0" class="task-view__completed">
                        <h3 class="task-view__completed-heading" data-panel-reveal>Completed</h3>
                        <div class="task-view__task-list">
                            <TaskCard
                                v-for="task in completedTasks"
                                :key="task.id"
                                :task="task"
                                data-panel-reveal
                            />
                        </div>
                    </section>
                </div>
            </div>

            <AppFooter />
        </template>

        <PaymentPurposeSheet
            :open="isPurposeSheetOpen"
            :purposes="initiablePurposes"
            :is-starting="isStartingPayment"
            @update:open="(val: boolean) => { if (!val) closePurposeSheet(); }"
            @select="startPayment"
        />

        <TaskPrimingSheet
            :open="isPrimingOpen"
            :task="primingTask"
            :config="primingConfig"
            :is-loading="isNavigating"
            :error="navigationError"
            @update:open="(val: boolean) => { if (!val) closePrimingSheet(); }"
            @continue="handlePrimingContinue"
        />
        <TaskSheet
            :open="isCompletionOpen"
            :task="completionTask"
            :view="completionView"
            @update:open="(val: boolean) => { if (!val) closeTaskSheet(); }"
        />
        <WelcomeSheet
            :open="dashboardStore.showWelcome"
            :is-submitting="isCapturing"
            :error="consentError"
            @submit="handleWelcomeSubmit"
        />
    </div>
</template>

<style lang="scss" scoped>
@use 'certifid-v2-client-hub-styles' as *;

.task-view {
    display: flex;
    flex-direction: column;
    min-height: 100vh;
    // Override the inherited `.mfe-container > * { height: 100% }` cap so the gray
    // page background grows with content; without it the background stops at one
    // viewport and the host's white body shows behind the footer once tasks scroll.
    height: auto;
    background: $portal-bg;

    // Mobile hero — white bg, welcome + property card
    &__hero {
        background: $portal-surface;
        padding: $portal-section-py $portal-section-px;
        display: flex;
        flex-direction: column;
        gap: 16px;
        box-shadow: 0 1px 3px color-mix(in srgb, $theme-primary-dark 6%, transparent);

        @media (min-width: 768px) {
            display: none;
        }
    }

    &__welcome {
        font-size: $text-portal-h4;
        font-weight: 700;
        color: $portal-text;
        margin: 0;
    }

    // Gray body on mobile, full white on desktop
    &__body {
        flex: 1;
        padding: $portal-section-py $portal-section-px 32px;

        @media (min-width: 768px) {
            background: $portal-surface;
            padding: 0;
            display: flex;
            justify-content: center;
        }
    }

    // Centered desktop column
    &__inner {
        display: flex;
        flex-direction: column;

        @media (min-width: 768px) {
            width: 100%;
            max-width: $portal-content-max-width;
            padding: 32px $portal-section-px;
        }
    }

    // Desktop-only welcome (hidden on mobile)
    &__desktop-welcome {
        display: none;
        margin-bottom: 24px;

        @media (min-width: 768px) {
            display: block;
        }
    }

    &__desktop-heading {
        font-size: $text-portal-h1;
        font-weight: 700;
        color: $portal-text;
        margin: 0 0 4px;
    }

    &__desktop-subtitle {
        font-size: $text-portal-caption;
        color: $portal-text-mid;
        margin: 0;
    }

    // Desktop-only deal card (hidden on mobile, shown in column)
    &__desktop-deal {
        display: none !important;
        margin-bottom: 32px;

        @media (min-width: 768px) {
            display: flex !important;
        }
    }

    &__payment-entry {
        margin-bottom: 16px;
    }

    &__tasks-heading {
        font-size: $text-portal-h4;
        font-weight: 700;
        color: $portal-text;
        margin: 0 0 16px;
    }

    &__task-list {
        display: flex;
        flex-direction: column;
        gap: $portal-card-gap;
    }

    &__completed {
        margin-top: 24px;
    }

    &__completed-heading {
        font-size: $text-portal-caption;
        font-weight: 700;
        color: $portal-text-mid;
        margin: 0 0 12px;
    }
}
</style>
